import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RouterIcon from '@mui/icons-material/Router';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Link as ReactLink } from 'react-router-dom';

import {
    Device,
    MAX_CELLULAR_SIGNAL_LEVEL,
    useDevice,
    useDeviceCellularSignalStatus,
    useDeviceSignalStatus,
    useDeviceSubscriptionStatus,
} from '@/api/devices';
import { ErrorIcon } from '@/components/StatusIcons';
import { DotLevelIndicator } from '@/components/data/DotLevelIndicator';
import { Timestamp } from '@/components/data/Timestamp';
import { CellularErrorAlert } from '@/components/spotter/CellularErrorAlert';
import { useSpotterContext } from '@/context/SpotterContext';
import { useIsSharedDevice } from '@/hooks/useIsSharedDevice';
import { spotterIdHash } from '@/hooks/useSpotterHash';

import { CellularSignalSection } from './CellularSignalSection';

const border = '1px solid rgba(192, 192, 192, 0.8)';

const regionForNoteSku = (noteSKU?: number) => {
    if (!noteSKU) {
        return 'Unknown';
    }
    switch (noteSKU) {
        case 1:
        case 3:
            return 'North America';
        case 2:
            return 'Europe, Middle East, Africa';
        case 4:
        case 5:
            return 'Global';
    }
    return 'Unknown';
};

const getCellularModemLabel = (device?: Device) => {
    const skuRegion = regionForNoteSku(device?.cellularData?.metadata?.noteSKU);
    const externalSim = device?.metadata?.externalSim;

    return { skuRegion, externalSim };
};

const ModifyTelemetryButton = ({ spotterId }: { spotterId: string | null }) => {
    if (!spotterId) {
        return null;
    }

    return (
        <Button
            component={ReactLink}
            variant="text"
            to={`/telemetry#${spotterId ? spotterIdHash(spotterId) : ''}`}
            sx={{ m: -1 }}
        >
            Modify telemetry
        </Button>
    );
};

export const TelemetrySection = () => {
    const { spotterId } = useSpotterContext();
    const isSharedDevice = useIsSharedDevice();
    const { data: device } = useDevice();
    const { data: signalStatus } = useDeviceSignalStatus();
    const { data: cellularSignalStatus } = useDeviceCellularSignalStatus();
    const { data: subscriptionStatus } = useDeviceSubscriptionStatus();

    const isEquippedWithCellular = device?.cellularData?.hasCellular;
    const isCellEnabled = subscriptionStatus?.cellEnabled;
    const lastUpdateSource = signalStatus?.lastUpdateSource;
    const sourceLabel =
        lastUpdateSource === 'iridium' ? 'satellite' : lastUpdateSource === 'cellular' ? 'cellular' : null;
    const cellularError = cellularSignalStatus?.error;
    const { skuRegion, externalSim } = getCellularModemLabel(device);

    const TelemetryStatusIcon = signalStatus?.isLateToCheckin ? ErrorIcon : CheckCircleIcon;
    const telemetryStatusColor = signalStatus?.isLateToCheckin ? 'error' : 'success';

    return (
        <Stack>
            <Typography variant="h4" mb={2}>
                Telemetry
            </Typography>

            <Box sx={{ border, p: 3 }}>
                <Stack gap={2}>
                    <Typography variant="h5">Telemetry status</Typography>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <TelemetryStatusIcon color={telemetryStatusColor} />
                        <Typography variant="body2">
                            Last checked in{' '}
                            <strong>
                                <Timestamp value={signalStatus?.lastUpdated ?? undefined} type="distanceToNow" />
                                {sourceLabel && ` via ${sourceLabel}`}
                            </strong>
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            {isEquippedWithCellular && (
                <>
                    {isCellEnabled && (
                        <Box sx={{ border, borderTop: 'none', p: 3 }}>
                            <Stack gap={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h5">Cellular signal status</Typography>
                                    {!!cellularSignalStatus?.timestamp && (
                                        <Typography variant="minorLabel">
                                            <Timestamp value={cellularSignalStatus.timestamp} type="distanceToNow" />
                                        </Typography>
                                    )}
                                </Stack>
                                {cellularSignalStatus && (
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <cellularSignalStatus.StatusIcon color={cellularSignalStatus.color} />
                                        <Typography variant="body2">{cellularSignalStatus.description}</Typography>
                                        {cellularSignalStatus.level > 0 && (
                                            <DotLevelIndicator
                                                level={cellularSignalStatus.level}
                                                maxLevel={MAX_CELLULAR_SIGNAL_LEVEL}
                                                color={cellularSignalStatus.color}
                                            />
                                        )}
                                    </Stack>
                                )}
                                {cellularError && <CellularErrorAlert error={cellularError} spotterId={spotterId} />}
                            </Stack>
                        </Box>
                    )}
                    <Box sx={{ border, borderTop: 'none' }}>
                        <CellularSignalSection />
                    </Box>
                    {!isSharedDevice && (
                        <Box sx={{ border, borderTop: 'none', p: 3 }}>
                            <Stack gap={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h5">Cellular status</Typography>
                                    <ModifyTelemetryButton spotterId={spotterId} />
                                </Stack>
                                <Stack gap={1}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        {isCellEnabled ? <CheckCircleIcon color="success" /> : <ErrorIcon />}
                                        <Typography variant="body2">
                                            Cellular telemetry{' '}
                                            {subscriptionStatus?.cellEnabled ? 'enabled' : 'disabled'}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <RouterIcon color="action" />
                                        <Typography variant="body2">
                                            Cellular modem region:{' '}
                                            <strong>
                                                {skuRegion}
                                                {externalSim ? ` (${externalSim})` : ''}
                                            </strong>
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Box>
                    )}
                </>
            )}
            {!isSharedDevice && (
                <Box sx={{ border, borderTop: 'none', p: 3 }}>
                    <Stack gap={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h5">Satellite status</Typography>
                            <ModifyTelemetryButton spotterId={spotterId} />
                        </Stack>
                        <Stack gap={1}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                {subscriptionStatus?.satEnabled ? <CheckCircleIcon color="success" /> : <ErrorIcon />}
                                <Typography variant="body2">
                                    Satellite telemetry {subscriptionStatus?.satEnabled ? 'enabled' : 'disabled'}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};
